"use client";

import { useEffect, useMemo, useState } from "react";
import mainAPI from "../../services/main/mainAPI";
import StatusPopUp from "../popup/statusPopUp";
import { MdKeyboardArrowDown } from "react-icons/md";
import { formatCompact2DateTime } from "../../utils/formatDatetime";
import CountAllGame from "../countAllGame";
import TablePagination, { type PageSizeOption } from "../shared/TablePagination";
import { useTranslations } from "next-intl";

type UserLogApiRow = {
    id?: number;
    id_game_info?: number;
    teacher_uuid?: string;
    exercise_name?: string;
    teacher_name?: string;
    create_at?: string;
};

type UserLogRow = {
    id: number;
    firstName: string;
    lastName: string;
    exerciseName: string;
    createAt: string;
};

type FilterState = {
    search: string;
    startDateTime: string;
};

const defaultFilters: FilterState = {
    search: "",
    startDateTime: "",
};

function isFailedResult(response: unknown) {
    return (response as { status?: string })?.status === "failed";
}

function splitTeacherName(fullName: string) {
    const cleaned = String(fullName ?? "").trim();
    if (!cleaned) {
        return { firstName: "-", lastName: "-" };
    }

    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: "-" };
    }

    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(" "),
    };
}

function normalizeRows(response: unknown): UserLogRow[] {
    const list = Array.isArray(response)
        ? response
        : Array.isArray((response as { data?: unknown[] })?.data)
            ? (response as { data: unknown[] }).data
            : [];

    return list
        .map((item) => {
            const row = item as UserLogApiRow;
            if (!row || typeof row.id !== "number") return null;

            const splitName = splitTeacherName(String(row.teacher_name ?? ""));
            return {
                id: row.id,
                firstName: splitName.firstName,
                lastName: splitName.lastName,
                exerciseName: String(row.exercise_name ?? "-").trim() || "-",
                createAt: String(row.create_at ?? "").trim(),
            };
        })
        .filter(Boolean) as UserLogRow[];
}

export default function UserLog() {
    const tMain = useTranslations("main");
    const [rows, setRows] = useState<UserLogRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState<FilterState>(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<PageSizeOption>(20);
    const [statusPopup, setStatusPopup] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);

    const loadUserLogs = async () => {
        setIsLoading(true);
        const response = await mainAPI.getAllUserCreatedLogs();

        if (!response || isFailedResult(response)) {
            setRows([]);
            setStatusPopup({
                title: "ไม่สำเร็จ",
                message: "โหลดข้อมูลการใช้งานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            setIsLoading(false);
            return;
        }

        setRows(normalizeRows(response));
        setIsLoading(false);
    };

    useEffect(() => {
        void loadUserLogs();
    }, []);

    const filteredRows = useMemo(() => {
        const keyword = filters.search.trim().toLowerCase();
        const selectedDate = filters.startDateTime ? new Date(filters.startDateTime).getTime() : null;

        return rows.filter((row) => {
            const matchSearch = !keyword || row.exerciseName.toLowerCase().includes(keyword);
            const rowTime = new Date(row.createAt).getTime();
            const matchDate = selectedDate === null || (Number.isFinite(rowTime) && rowTime >= selectedDate);

            return matchSearch && matchDate;
        });
    }, [rows, filters]);

    const totalCount = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters.search, filters.startDateTime, pageSize]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    return (
        <div className="space-y-4">
            <CountAllGame />

            <div className="p-1">
                <p className="mb-2 text-md font-bold text-[#0092D7]">Filter by:</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-[#4E4E4E]">ค้นหา</label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(event) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    search: event.target.value,
                                }))
                            }
                            placeholder="ค้นหา"
                            className="h-10 w-full rounded-md border border-[#d7d7d7] bg-white px-3 text-sm text-[#4E4E4E] outline-none"
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-[#4E4E4E]">ช่วงเวลา</label>
                        <div className="relative">
                            <input
                                type="datetime-local"
                                value={filters.startDateTime}
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        startDateTime: event.target.value,
                                    }))
                                }
                                className="h-10 w-full rounded-md border border-[#d7d7d7] bg-white px-3 pr-10 text-sm text-[#4E4E4E] outline-none"
                            />
                            <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xl text-[#9a9a9a]" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white pb-4">
                <div className="px-4 py-2 text-lg font-bold text-[#555555]">การใช้งานทั้งหมด</div>

                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                        <thead>
                            <tr className="bg-[#F8F8F8]">
                                <th className="w-12 px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">#</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">ชื่อ</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">นามสกุล</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">เวลาที่สร้าง</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">กิจกรรม</th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-lg text-[#9a9a9a]">
                                        กำลังโหลดข้อมูล...
                                    </td>
                                </tr>
                            ) : filteredRows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-lg text-[#9a9a9a]">
                                        ไม่พบข้อมูลการใช้งาน
                                    </td>
                                </tr>
                            ) : (
                                paginatedRows.map((row, index) => (
                                    <tr key={row.id} className={index % 2 === 0 ? "bg-[#EEEFF6]" : "bg-[#F8F8F8]"}>
                                        <td className="px-4 py-2 text-md text-[#555555]">{(currentPage - 1) * pageSize + index + 1}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{row.firstName}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{row.lastName}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{formatCompact2DateTime(row.createAt) || "-"}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{row.exerciseName}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!isLoading && filteredRows.length > 0 ? (
                    <TablePagination
                        totalCount={totalCount}
                        totalCountLabel={tMain("totalRecords", { count: totalCount })}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                    />
                ) : null}
            </div>

            {statusPopup ? (
                <StatusPopUp
                    title={statusPopup.title}
                    message={statusPopup.message}
                    type={statusPopup.type}
                    onClose={() => setStatusPopup(null)}
                />
            ) : null}
        </div>
    );
}
